import { api } from "./axios";

export type GenerateOtpRequest = Partial<{
  phone_number: string;
  email: string;
  arn_code: string;
  username: string;
  investment_account_guid: string;
  profile_id: string;
}>;

/** Distributor employee usernames use a `.` in the handle (e.g. ARN-org.jane). */
export function isDistributorEmployeeLoginIdentifier(
  arnOrUsername: string
): boolean {
  return arnOrUsername.trim().includes(".");
}

/** Employee login/OTP APIs expect `ARN-` + handle (form value may omit the prefix). */
export function toDistributorEmployeeLoginUsername(arnCode: string): string {
  const handle = arnCode.trim().replace(/^ARN-/i, "");
  return `ARN-${handle}`;
}

/**
 * `otp/generate/` expects `username` (with `ARN-` prefix) for distributor employee
 * handles; otherwise `arn_code`.
 */
export function distributorOtpGeneratePayload(
  arnOrEmployeeHandle: string
): { arn_code: string } | { username: string } {
  const v = arnOrEmployeeHandle.trim();
  if (isDistributorEmployeeLoginIdentifier(arnOrEmployeeHandle)) {
    return {
      username: toDistributorEmployeeLoginUsername(arnOrEmployeeHandle),
    };
  }
  return { arn_code: v };
}

export type UserType = "investor" | "distributor" | "employee";

export type UserStatus =
  | "user_created"
  | "user_verified"
  | "user_activated"
  | "user_deactivated"
  | "user_deleted";

export type User = {
  user_guid: string;
  user_type: UserType;
  user_status: UserStatus;
  username: string;
  metadata: unknown;
  date_joined: string;
};

export function generateOtp(payload: GenerateOtpRequest) {
  return api.post("otp/generate/", payload);
}

export async function me(): Promise<User> {
  return api.get("me/");
}

export function logout() {
  return api.post("logout/");
}

export function refreshToken(payload: { refresh: string }) {
  return api.post("token/refresh/", payload);
}

export type SignUpRequest = {
  phone_number: string;
  phone_otp: string;
  email: string;
  email_otp: string;
};

export function register(payload: SignUpRequest) {
  return api.post("investor/signup/", payload);
}

export type DistributorLoginRequest = {
  arn_code: string;
  password_or_otp: string;
};

export function distributorLogin(payload: DistributorLoginRequest) {
  return api.post("distributor/login/", payload);
}

export type ResetPasswordRequest = {
  new_password: string;
};

export function resetPassword(payload: ResetPasswordRequest) {
  return api.post("distributor-reset-password/", payload);
}

export type DistributorEmployeeSetPasswordRequest = {
  new_password: string;
};

export function setDistributorEmployeePassword(
  payload: DistributorEmployeeSetPasswordRequest
) {
  return api.post("distributor-employee/reset-password/", payload);
}

/** Invite/session bootstrap, or password/OTP login for team members (username often `ARN-….suffix`). */
export type DistributorEmployeeLoginRequest =
  | { verification_token: string }
  | {
      username: string;
      password_or_otp: string;
      verification_token?: string;
    };

export function distributorEmployeeLogin(
  payload: DistributorEmployeeLoginRequest
) {
  return api.post("distributor-employee/login/", payload);
}

export type InvestorLoginRequest = {
  phone_number: string;
  otp: string;
  code: string;
};

export async function investorLogin(
  payload: Partial<InvestorLoginRequest>
): Promise<{
  investment_account_guid: string;
  message: string;
  redirect_url?: string;
}> {
  const result = await api.post("investor/login/", payload);
  if (result.redirect_url) {
    // web-only patch to make sure all the stand alone routes that logs in via code, redirects
    // to the right domain if it needs to.
    window.location.replace(result.redirect_url);
  }
  return result;
}

export interface VerifyDistributorEmailArnPayload {
  arn: string;
  email: string;
}

export interface VerifyDistributorEmailArnResponse {
  is_valid: boolean;
}

export function verifyDistributorEmailArn(
  query: VerifyDistributorEmailArnPayload = { arn: "", email: "" }
): Promise<VerifyDistributorEmailArnResponse> {
  const arn = query.arn.replace(/^ARN-/, "");
  return api.get(`verify-arn-email/?arn=${arn}&email=${query.email}`);
}

export const requestConsentToAddClient = (payload: { client_id: number }) =>
  api.post("v2/request-consent/", payload);

export const verifyConsentApi = (payload: {
  code: string;
  regenerateCode?: boolean;
}) =>
  api.get(`v2/verify-consent/${payload.code}/`, {
    params: { send_code: payload.regenerateCode },
  });

export interface SignupDistributorPayload {
  email: string;
  phone_number: string;
  email_otp: string;
  phone_otp: string;
  password?: string;
  arn_code?: string;
  referred_by_handle?: string;
  utm_details?: Record<string, string>;
}

export const signupDistributorApi = (payload: SignupDistributorPayload) => {
  return api.post("distributor/signup/", payload);
};

export const resetDistributorPassword = (payload: { new_password: string }) => {
  return api.post("distributor-reset-password/", payload);
};

export const authenticateOtp = (payload: {
  otp: string;
  email?: string;
  phone_number?: string;
}) => {
  return api.post("authenticate-otp/", payload);
};
